// ============================================================
// Supabase Auth — thin client wrapper.
// Gates the app; a signed-in user joins the demo project so the
// authenticated RLS policies grant them data access.
// ============================================================
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

export async function getSession(): Promise<Session | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export function onAuthChange(cb: (session: Session | null) => void): () => void {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_e, session) => cb(session));
  return () => data.subscription.unsubscribe();
}

export async function signUp(email: string, password: string, fullName: string) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName } },
  });
  if (error) throw error;
  return data;
}

export async function signIn(email: string, password: string) {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

/** The signed-in user's auth id (null when offline / not signed in). */
export async function getMyUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

/** Add the signed-in user to the Ballyroe demo project's members. */
export async function joinDemoProject(role = "qcmanager"): Promise<void> {
  if (!supabase) return;
  await supabase.rpc("join_demo_project", { p_role: role });
}
